import { Duration, type DurationLike } from "@shared/types/duration"

import type { StorageStrategy } from "./StorageStrategy"

interface TTLEnvelope {
  __value: string
  __expiresAt: number
}

function isEnvelope(value: unknown): value is TTLEnvelope {
  return typeof value === "object" && value !== null && "__value" in value && "__expiresAt" in value
}

/**
 * Decorator that adds time-to-live (TTL) expiration to any {@link StorageStrategy}.
 *
 * Values are wrapped in a `{ __value, __expiresAt }` JSON envelope before being
 * stored via the inner strategy. On read, expired values are treated as missing.
 *
 * @example
 * ```ts
 * // Session storage with 30-minute expiry
 * const strategy = new TTLStorageStrategy(sessionStorageStrategy, "30 minutes")
 *
 * // Can wrap any strategy
 * const ttlCookies = new TTLStorageStrategy(cookieStorageStrategy, { hours: 1 })
 * ```
 */
export class TTLStorageStrategy implements StorageStrategy {
  private readonly ttlMs: number

  constructor(
    private readonly inner: StorageStrategy,
    ttl: DurationLike
  ) {
    this.ttlMs = Duration.from(ttl).milliseconds
  }

  getItem(key: string): string | undefined {
    const raw = this.inner.getItem(key)
    if (raw === undefined) {
      return undefined
    }

    try {
      const parsed = JSON.parse(raw)
      if (isEnvelope(parsed) && parsed.__expiresAt > Date.now()) {
        return parsed.__value
      }
      return undefined
    } catch {
      return undefined
    }
  }

  setItem(key: string, value: string): void {
    const envelope: TTLEnvelope = { __value: value, __expiresAt: Date.now() + this.ttlMs }
    this.inner.setItem(key, JSON.stringify(envelope))
  }

  deleteItem(key: string): void {
    this.inner.deleteItem(key)
  }
}
